import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';

const NotificationSender = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendType, setSendType] = useState('user'); // 'user' or 'ranks'
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRanks, setSelectedRanks] = useState([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [testingSending, setTestingSending] = useState(false);
  const [testingAvailability, setTestingAvailability] = useState(false);
  const [testingEmailAlerts, setTestingEmailAlerts] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);

  const [ranks, setRanks] = useState([]);

    useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        const response = await apiClient.get('/api/user/permissions');
        setUserPermissions(response.data);
      } catch (error) {
        console.error('Failed to fetch user permissions:', error);
      }
    };

    const fetchRanks = async () => {
      try {
        const response = await apiClient.get('/api/ranks/list');
        setRanks(response.data.map(rank => rank.display_name));
      } catch (error) {
        console.error('Failed to fetch ranks:', error);
        // Fallback to hardcoded ranks
        setRanks(['Captain', 'Lieutenant', 'Sergeant', 'Lifeguard', 'Rookie']);
      }
    };

    if (user) {
      fetchUserPermissions();
      fetchRanks();
    }
  }, [user]);

  useEffect(() => {
    if (userPermissions.includes('send_notifications')) {
      fetchUsers();
    }
  }, [userPermissions]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRankToggle = (rank) => {
    setSelectedRanks(prev => 
      prev.includes(rank) 
        ? prev.filter(r => r !== rank)
        : [...prev, rank]
    );
  };

  const sendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      alert('Please fill in both title and message fields.');
      return;
    }

    if (sendType === 'user' && !selectedUser) {
      alert('Please select a user to send the notification to.');
      return;
    }

    if (sendType === 'ranks' && selectedRanks.length === 0) {
      alert('Please select at least one rank to send the notification to.');
      return;
    }

    setSending(true);
    try {
      let response;
      if (sendType === 'user') {
        response = await apiClient.post('/api/push/send-to-user', {
          userId: selectedUser,
          title: title.trim(),
          body: body.trim(),
          data: {
            type: 'manual_notification',
            sender: user.username,
            timestamp: Date.now()
          }
        });
      } else {
        response = await apiClient.post('/api/push/send-to-ranks', {
          ranks: selectedRanks,
          title: title.trim(),
          body: body.trim(),
          data: {
            type: 'manual_notification',
            sender: user.username,
            timestamp: Date.now()
          }
        });
      }

      alert(`✅ ${response.data.message}`);
      
      // Clear form
      setTitle('');
      setBody('');
      setSelectedUser('');
      setSelectedRanks([]);
      
    } catch (error) {
      console.error('Failed to send notification:', error);
      alert(`❌ Failed to send notification: ${error.response?.data?.error || error.message}`);
    } finally {
      setSending(false);
    }
  };

  const testTomorrowNotifications = async () => {
    setTestingSending(true);
    try {
      const response = await apiClient.post('/api/push/test-tomorrow-notifications');
      alert(`✅ ${response.data.message}\n\nCheck the server console for detailed results.`);
    } catch (error) {
      console.error('Failed to test tomorrow notifications:', error);
      alert(`❌ Failed to test tomorrow notifications: ${error.response?.data?.error || error.message}`);
    } finally {
      setTestingSending(false);
    }
  };

  const testAvailabilityReminders = async () => {
    setTestingAvailability(true);
    try {
      const response = await apiClient.post('/api/push/test-availability-reminders');
      alert(`✅ ${response.data.message}\n\nCheck the server console for detailed results.`);
    } catch (error) {
      console.error('Failed to test availability reminders:', error);
      alert(`❌ Failed to test availability reminders: ${error.response?.data?.error || error.message}`);
    } finally {
      setTestingAvailability(false);
    }
  };

  const testScheduleEmailAlerts = async () => {
    setTestingEmailAlerts(true);
    try {
      const response = await apiClient.post('/api/push/test-schedule-email-alerts');
      alert(`✅ ${response.data.message}\n\nCheck the server console for detailed results.`);
    } catch (error) {
      console.error('Failed to test schedule email alerts:', error);
      alert(`❌ Failed to test schedule email alerts: ${error.response?.data?.error || error.message}`);
    } finally {
      setTestingEmailAlerts(false);
    }
  };

  // Only show to users with send_notifications permission
  if (!user || !userPermissions.includes('send_notifications')) {
    return null;
  }

  return (
    <div style={{ 
      margin: '20px 0', 
      padding: '20px', 
      border: '1px solid #ddd', 
      borderRadius: '8px',
      backgroundColor: '#f8f9fa'
    }}>
      <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>
        📢 Send Push Notification
      </h3>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Send To:
        </label>
        <div style={{ display: 'flex', gap: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="user"
              checked={sendType === 'user'}
              onChange={(e) => setSendType(e.target.value)}
              style={{ marginRight: '5px' }}
            />
            Specific User
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="ranks"
              checked={sendType === 'ranks'}
              onChange={(e) => setSendType(e.target.value)}
              style={{ marginRight: '5px' }}
            />
            By Rank {!userPermissions.includes('send_notifications_all') && '(Limited access)'}
          </label>
        </div>
      </div>

      {sendType === 'user' && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Select User:
          </label>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
            disabled={loading}
          >
            <option value="">Choose a user...</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name} ({u.username}) - {u.rank}
              </option>
            ))}
          </select>
        </div>
      )}

      {sendType === 'ranks' && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Select Ranks:
          </label>
          {userPermissions.includes('send_notifications_all') ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {ranks.map(rank => (
                <label key={rank} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedRanks.includes(rank)}
                    onChange={() => handleRankToggle(rank)}
                    style={{ marginRight: '5px' }}
                  />
                  {rank}
                </label>
              ))}
            </div>
          ) : (
            <p style={{ color: '#666', fontStyle: 'italic' }}>
              You need additional permissions to send notifications to multiple ranks.
            </p>
          )}
        </div>
      )}

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Title:
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Schedule Update, Important Announcement"
          maxLength={50}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <small style={{ color: '#666' }}>
          {title.length}/50 characters
        </small>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Message:
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Enter your notification message here..."
          maxLength={200}
          rows={4}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            resize: 'vertical',
            fontFamily: 'inherit'
          }}
        />
        <small style={{ color: '#666' }}>
          {body.length}/200 characters
        </small>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <button
          onClick={sendNotification}
          disabled={sending || !title.trim() || !body.trim()}
          style={{
            backgroundColor: sending ? '#ccc' : '#2196f3',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '5px',
            cursor: sending ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          {sending ? '📤 Sending...' : '📤 Send Notification'}
        </button>

        <div style={{ fontSize: '12px', color: '#666' }}>
          <strong>Preview:</strong><br />
          Title: {title || 'Enter title'}<br />
          Message: {body || 'Enter message'}
        </div>
      </div>

      {/* Test Automated Notifications Buttons */}
      <div style={{ 
        marginBottom: '15px', 
        padding: '15px', 
        backgroundColor: '#fff3cd', 
        border: '1px solid #ffeaa7', 
        borderRadius: '5px' 
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>
          🧪 Test Automated Notifications
        </h4>
        <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#856404' }}>
          Test the automated daily notification systems that run at 5:00 PM EST.
        </p>
        
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={testTomorrowNotifications}
            disabled={testingSending}
            style={{
              backgroundColor: testingSending ? '#ccc' : '#ff9800',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: testingSending ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {testingSending ? '⏳ Testing...' : '🔔 Test Tomorrow Notifications'}
          </button>
          
          <button
            onClick={testAvailabilityReminders}
            disabled={testingAvailability}
            style={{
              backgroundColor: testingAvailability ? '#ccc' : '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: testingAvailability ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {testingAvailability ? '⏳ Testing...' : '📋 Test Availability Reminders'}
          </button>
          
          <button
            onClick={testScheduleEmailAlerts}
            disabled={testingEmailAlerts}
            style={{
              backgroundColor: testingEmailAlerts ? '#ccc' : '#6f42c1',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: testingEmailAlerts ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {testingEmailAlerts ? '⏳ Testing...' : '📧 Test Email Alerts'}
          </button>
        </div>
        
        <div style={{ marginTop: '10px', fontSize: '12px', color: '#856404' }}>
          <p style={{ margin: '5px 0' }}>
            <strong>Tomorrow Notifications:</strong> Notifies users scheduled to work tomorrow.
          </p>
          <p style={{ margin: '5px 0' }}>
            <strong>Availability Reminders:</strong> Reminds users who haven't marked at least 7 days of availability.
          </p>
          <p style={{ margin: '5px 0' }}>
            <strong>Email Alerts:</strong> Sends emails to users with schedule alerts enabled who are working tomorrow.
          </p>
        </div>
      </div>

      <div style={{ marginTop: '15px', fontSize: '12px', color: '#888' }}>
        <strong>Note:</strong> Notifications will only be sent to users who have:
        <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
          <li>Added the app to their device homescreen</li>
          <li>Enabled push notifications</li>
          <li>An active device subscription</li>
        </ul>
      </div>
    </div>
  );
};

export default NotificationSender; 